import { useEffect, useState } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { dist, scaleFromReference } from "./drawingAnnotationUtils";

export const DrawingCalibrationDialog = ({
  t,
  open,
  referenceLine,
  scale,
  onClose,
  onApply,
  saving,
}) => {
  const [knownMm, setKnownMm] = useState("");
  const [errorKey, setErrorKey] = useState(null);

  useEffect(() => {
    if (!open) {
      setKnownMm("");
      setErrorKey(null);
    }
  }, [open]);

  if (!open) return null;

  const pdfDistance = referenceLine
    ? dist(referenceLine.x1, referenceLine.y1, referenceLine.x2, referenceLine.y2)
    : 0;
  const knownValue = parseFloat(knownMm);
  const hasKnown = Number.isFinite(knownValue) && knownValue > 0;
  const previewScale =
    hasKnown && pdfDistance > 0
      ? scaleFromReference(pdfDistance, knownValue)
      : null;

  const handleApply = () => {
    if (!referenceLine || pdfDistance <= 0) {
      setErrorKey("drawings.viewer.calibrate_error_no_line");
      return;
    }
    if (!hasKnown) {
      setErrorKey("drawings.viewer.calibrate_error_no_length");
      return;
    }
    if (!previewScale) {
      setErrorKey("drawings.viewer.calibrate_error_invalid");
      return;
    }
    setErrorKey(null);
    onApply(previewScale);
  };

  // No reference line yet: non-blocking banner so the canvas stays drawable.
  if (!referenceLine) {
    return (
      <div className="pointer-events-none fixed inset-x-0 bottom-[calc(4.5rem+env(safe-area-inset-bottom,0px))] z-[60] flex justify-center px-3 md:bottom-6">
        <div className="pointer-events-auto flex max-w-lg items-start gap-3 rounded-[14px] border border-[#2b6ef0]/35 bg-white px-3.5 py-3 shadow-[0_12px_40px_rgba(15,23,42,0.18)]">
          <div className="min-w-0 flex-1">
            <div className="text-[13px] font-semibold text-[#14171c]">
              {t("drawings.viewer.calibrate_title")}
            </div>
            <p className="mt-0.5 text-[12px] text-[#59616d]">
              {t("drawings.viewer.calibrate_draw_line")}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="grid h-8 w-8 flex-none place-items-center rounded-lg text-[#59616d] hover:bg-[#eef0f3]"
            aria-label={t("Cancel")}
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-stretch justify-center bg-black/40 p-0 sm:items-center sm:p-4">
      <div className="flex h-[100dvh] max-h-[100dvh] w-full max-w-md flex-col overflow-hidden rounded-none border-0 border-[#e5e8ec] bg-white shadow-xl sm:h-auto sm:max-h-[min(92dvh,640px)] sm:rounded-xl sm:border">
        <div className="flex flex-shrink-0 items-center justify-between border-b border-[#e5e8ec] px-3 py-2 sm:px-4 sm:py-3">
          <h2 className="text-sm font-semibold text-[#14171c]">
            {t("drawings.viewer.calibrate_title")}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="grid h-9 w-9 place-items-center rounded-lg text-[#59616d] hover:bg-[#eef0f3] sm:h-8 sm:w-8"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-3 py-2.5 text-[13px] text-[#59616d] sm:space-y-4 sm:px-4 sm:py-4">
          <p>{t("drawings.viewer.calibrate_help")}</p>

          <div className="rounded-lg bg-[#f6f7f9] px-3 py-2 font-mono text-xs text-[#14171c]">
            {t("drawings.viewer.calibrate_line_length")}: {Math.round(pdfDistance)} pt
          </div>

          <label className="block">
            <span className="mb-1 block text-[11px] font-bold uppercase tracking-[0.05em] text-[#969da8]">
              {t("drawings.viewer.calibrate_known_mm")}
            </span>
            <input
              type="number"
              min="0"
              step="0.1"
              value={knownMm}
              onChange={(event) => {
                setKnownMm(event.target.value);
                setErrorKey(null);
              }}
              className="w-full rounded-lg border border-[#e5e8ec] px-3 py-2 text-[#14171c] outline-none focus:border-[#2b6ef0]"
              placeholder="1000"
              autoFocus
            />
          </label>

          <div className="rounded-lg bg-[#eaf1fe] px-3 py-2 text-xs text-[#1d4fb0]">
            {t("drawings.viewer.calibrate_current_scale")}: {scale?.toFixed(4)} mm/pt
            {previewScale ? (
              <>
                <br />
                {t("drawings.viewer.calibrate_new_scale")}: {previewScale.toFixed(4)} mm/pt
              </>
            ) : null}
          </div>

          {errorKey && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs font-medium text-red-700">
              {t(errorKey)}
            </div>
          )}
          {!hasKnown && !errorKey && (
            <div className="text-xs text-[#969da8]">
              {t("drawings.viewer.calibrate_error_no_length")}
            </div>
          )}
        </div>

        <div className="flex flex-shrink-0 justify-end gap-2 border-t border-[#e5e8ec] px-3 py-2.5 pb-[calc(0.65rem+env(safe-area-inset-bottom,0px))] sm:px-4 sm:py-3 sm:pb-3">
          <button
            type="button"
            onClick={onClose}
            className="h-10 rounded-lg border border-[#e5e8ec] px-3 text-sm font-semibold text-[#14171c] hover:bg-[#f6f7f9] sm:h-auto sm:py-2"
          >
            {t("Cancel")}
          </button>
          <button
            type="button"
            disabled={!previewScale || saving}
            onClick={handleApply}
            className="h-10 rounded-lg bg-[#2b6ef0] px-3 text-sm font-semibold text-white disabled:opacity-50 sm:h-auto sm:py-2"
          >
            {saving ? t("Saving...") : t("drawings.viewer.calibrate_save")}
          </button>
        </div>
      </div>
    </div>
  );
};
